import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Bell, BellOff, CalendarDays, Heart, Loader2, MapPin, Mic, PenLine, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { AppHeader } from "@/components/AppHeader";
import { Avatar } from "@/components/Avatar";
import { MILESTONE_CATEGORIES } from "@/pages/Milestones";
import { useFamily } from "@/lib/useFamily";
import { formatDate } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MediaBlock({ memory }) {
    const src = memory.storage_path ? `${API}/files/${memory.storage_path}` : "";
    if (memory.memory_type === "photo") {
        return (
            <img
                data-testid="memory-media"
                src={src}
                alt={memory.title || memory.caption || "Family memory"}
                className="max-h-[70vh] w-full rounded-3xl border-2 border-warmgray object-contain bg-navy"
            />
        );
    }
    if (memory.memory_type === "video") {
        return (
            <video
                data-testid="memory-media"
                src={src}
                controls
                preload="metadata"
                className="max-h-[70vh] w-full rounded-3xl border-2 border-warmgray bg-navy"
            />
        );
    }
    if (memory.memory_type === "audio") {
        return (
            <div
                data-testid="memory-media"
                className="flex w-full flex-col items-center justify-center rounded-3xl bg-gradient-to-br from-lavender to-peach p-10 md:p-14"
            >
                <Mic size={44} className="text-white" />
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.3em] text-white/90">A voice from your family</p>
                <audio data-testid="memory-audio-player" src={src} controls className="mt-6 w-full max-w-md" />
            </div>
        );
    }
    return (
        <div data-testid="memory-media" className="flex w-full items-center justify-center rounded-3xl border-2 border-warmgray bg-ivory p-8">
            <PenLine size={36} className="text-peach" />
        </div>
    );
}

function TranscriptBlock({ memory, canEdit, onChanged }) {
    const [requesting, setRequesting] = useState(false);
    const status = memory.transcription_status || "";

    useEffect(() => {
        if (status !== "pending") return;
        const t = setTimeout(onChanged, 6000);
        return () => clearTimeout(t);
    }, [status, onChanged]);

    const request = async () => {
        setRequesting(true);
        try {
            await axios.post(`${API}/memories/${memory.memory_id}/transcribe`, {}, { withCredentials: true });
            toast.success("Transcription started — it usually takes a few seconds.");
            setTimeout(onChanged, 5000);
        } catch {
            toast.error("Could not start transcription.");
        }
        setRequesting(false);
    };

    if (memory.transcript) {
        return (
            <div data-testid="memory-transcript" className="mt-6 rounded-2xl border-2 border-warmgray bg-ivory p-6">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                    Transcript{memory.transcript_language ? ` · ${memory.transcript_language.toUpperCase()}` : ""}
                </p>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-navy">{memory.transcript}</p>
                {memory.translation && (
                    <div className="mt-4 border-t border-warmgray pt-4">
                        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-lavender">English translation · AI</p>
                        <p data-testid="memory-translation" className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#2C3347]">{memory.translation}</p>
                    </div>
                )}
            </div>
        );
    }
    if (status === "pending") {
        return (
            <p data-testid="transcript-pending" className="mt-6 flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-warmgray bg-ivory p-4 text-sm text-navy/70">
                <Loader2 size={14} className="animate-spin" /> Transcription in progress — it will appear here in a moment.
            </p>
        );
    }
    if (canEdit) {
        return (
            <div className="mt-6">
                <button
                    data-testid="transcript-generate-button"
                    onClick={request}
                    disabled={requesting}
                    className="flex items-center gap-2 rounded-full border-2 border-warmgray px-5 py-2.5 text-xs font-semibold text-navy transition-colors duration-300 hover:border-peach disabled:opacity-60"
                >
                    {requesting && <Loader2 size={13} className="animate-spin" />}
                    {status === "failed" ? "Retry transcript" : "Generate transcript"}
                </button>
            </div>
        );
    }
    return null;
}

export default function MemoryDetail() {
    const { memoryId } = useParams();
    const navigate = useNavigate();
    const { checking, user } = useFamily();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [commentFile, setCommentFile] = useState(null);
    const [recording, setRecording] = useState(false);
    const [sending, setSending] = useState(false);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/memories/${memoryId}`, { withCredentials: true });
            setData(data);
        } catch {
            setNotFound(true);
        }
    }, [memoryId]);

    useEffect(() => {
        if (!checking) load();
    }, [checking, load]);

    const toggleHeart = async () => {
        try {
            const { data: res } = await axios.post(`${API}/memories/${memoryId}/heart`, {}, { withCredentials: true });
            setData((d) => ({ ...d, heart_count: res.heart_count, hearted_by_me: res.hearted }));
        } catch {
            toast.error("Could not save your heart.");
        }
    };

    const toggleMute = async () => {
        try {
            const { data: res } = await axios.post(`${API}/memories/${memoryId}/mute`, {}, { withCredentials: true });
            setData((d) => ({ ...d, muted_by_me: res.muted }));
            toast.success(res.muted ? "Email alerts muted for this memory." : "Email alerts unmuted for this memory.");
        } catch {
            toast.error("Could not change the alert setting.");
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => chunksRef.current.push(e.data);
            mr.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                const ext = (mr.mimeType || "").includes("mp4") ? "mp4" : "webm";
                setCommentFile(new File([blob], `voice-comment-${Date.now()}.${ext}`, { type: blob.type }));
                stream.getTracks().forEach((t) => t.stop());
            };
            mr.start();
            mediaRecorderRef.current = mr;
            setRecording(true);
        } catch {
            toast.error("Microphone access is needed to record a voice comment.");
        }
    };

    const stopRecording = () => {
        mediaRecorderRef.current?.stop();
        setRecording(false);
    };

    const sendComment = async (e) => {
        e.preventDefault();
        if (!commentText.trim() && !commentFile) return;
        setSending(true);
        try {
            const form = new FormData();
            form.append("text", commentText);
            if (commentFile) form.append("file", commentFile);
            await axios.post(`${API}/memories/${memoryId}/comments`, form, { withCredentials: true });
            setCommentText("");
            setCommentFile(null);
            await load();
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not post your comment.");
        }
        setSending(false);
    };

    const remove = async () => {
        try {
            await axios.delete(`${API}/memories/${memoryId}`, { withCredentials: true });
            toast.success("Memory removed from your family archive.");
            navigate("/dashboard", { replace: true });
        } catch {
            toast.error("Could not delete this memory.");
        }
    };

    if (checking || (!data && !notFound)) {
        return (
            <main data-testid="memory-detail-loading" className="flex min-h-screen items-center justify-center bg-cream">
                <Loader2 size={32} className="animate-spin text-lavender" />
            </main>
        );
    }

    if (notFound) {
        return (
            <main data-testid="memory-detail-not-found" className="min-h-screen bg-cream text-navy">
                <AppHeader user={user} />
                <div className="mx-auto max-w-3xl px-6 py-24 text-center">
                    <h1 className="font-display text-3xl">This memory was not found.</h1>
                    <Link to="/dashboard" data-testid="back-to-dashboard" className="mt-4 inline-block text-sm font-medium underline-offset-4 hover:underline">
                        Back to your archive
                    </Link>
                </div>
            </main>
        );
    }

    const { memory, author, tagged, milestone, comments, heart_count, hearted_by_me, muted_by_me, can_delete } = data;
    const MilestoneIcon = milestone ? (MILESTONE_CATEGORIES[milestone.category] || MILESTONE_CATEGORIES.other).icon : null;

    return (
        <main data-testid="memory-detail-page" className="min-h-screen bg-cream text-navy">
            <AppHeader user={user} />
            <div className="mx-auto max-w-3xl px-6 py-10">
                <Link
                    to="/dashboard"
                    data-testid="back-to-dashboard"
                    className="inline-flex items-center gap-2 text-sm font-medium text-navy/70 transition-colors duration-300 hover:text-navy"
                >
                    <ArrowLeft size={15} /> Back to archive
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                    className="mt-6"
                >
                    <MediaBlock memory={memory} />
                    <div className="mt-8">
                        {memory.title && (
                            <h1 data-testid="memory-title" className="font-display text-3xl tracking-tight md:text-4xl">{memory.title}</h1>
                        )}
                        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                            <span data-testid="memory-author" className="flex items-center gap-2.5">
                                <Avatar person={author || { name: memory.author_name }} size={36} />
                                <span>
                                    <span className="block text-sm font-semibold">{memory.author_name}</span>
                                    <span className="block font-mono text-[10px] uppercase tracking-widest text-navy/60">
                                        Added {formatDate(memory.created_at)}
                                    </span>
                                </span>
                            </span>
                            {memory.memory_date && (
                                <span data-testid="memory-date" className="flex items-center gap-1.5 text-sm text-[#2C3347]">
                                    <CalendarDays size={14} className="text-peach" /> {formatDate(memory.memory_date, { month: "long", day: "numeric", year: "numeric" })}
                                </span>
                            )}
                            {memory.location && (
                                <span data-testid="memory-location" className="flex items-center gap-1.5 text-sm text-[#2C3347]">
                                    <MapPin size={14} className="text-peach" /> {memory.location}
                                </span>
                            )}
                        </div>

                        {memory.story && (
                            <p data-testid="memory-story" className="mt-6 whitespace-pre-line text-base leading-relaxed text-navy">
                                {memory.story}
                            </p>
                        )}
                        {memory.caption && (
                            <p data-testid="memory-caption" className="mt-4 text-sm font-medium leading-relaxed text-[#2C3347]">
                                {memory.caption}
                            </p>
                        )}

                        {memory.memory_type === "audio" && (
                            <TranscriptBlock memory={memory} canEdit={can_delete} onChanged={load} />
                        )}

                        {(tagged.length > 0 || milestone) && (
                            <div className="mt-6 flex flex-wrap items-center gap-2">
                                {milestone && (
                                    <Link
                                        to={`/milestones/${milestone.milestone_id}`}
                                        data-testid="memory-milestone-tag"
                                        className="flex items-center gap-1.5 rounded-full bg-gradient-to-br from-lavender to-peach px-4 py-1.5 text-xs font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                                    >
                                        {MilestoneIcon && <MilestoneIcon size={13} />}
                                        {milestone.title}
                                    </Link>
                                )}
                                {tagged.map((t) => (
                                    <Link
                                        key={t.user_id}
                                        to={`/dashboard?tagged=${t.user_id}`}
                                        data-testid={`memory-tagged-${t.user_id}`}
                                        className="flex items-center gap-1.5 rounded-full border-2 border-warmgray bg-ivory py-1 pl-1 pr-3 text-xs font-semibold text-navy transition-colors duration-300 hover:border-peach"
                                    >
                                        <Avatar person={t} size={20} />
                                        {t.name}
                                    </Link>
                                ))}
                            </div>
                        )}

                        <div className="mt-8 flex items-center gap-3 border-t border-warmgray pt-6">
                            <button
                                data-testid="heart-button"
                                onClick={toggleHeart}
                                aria-label="Heart this memory"
                                className={`flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                                    hearted_by_me ? "border-peach bg-peach text-white" : "border-warmgray text-navy hover:border-peach"
                                }`}
                            >
                                <Heart size={16} fill={hearted_by_me ? "currentColor" : "none"} />
                                <span data-testid="heart-count">{heart_count}</span>
                            </button>
                            {memory.user_id === user.user_id && (
                                <button
                                    data-testid="mute-button"
                                    onClick={toggleMute}
                                    aria-label={muted_by_me ? "Unmute email alerts for this memory" : "Mute email alerts for this memory"}
                                    className={`flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                                        muted_by_me ? "border-navy bg-navy text-white" : "border-warmgray text-navy hover:border-peach"
                                    }`}
                                >
                                    {muted_by_me ? <BellOff size={16} /> : <Bell size={16} />}
                                    {muted_by_me ? "Muted" : "Mute alerts"}
                                </button>
                            )}
                            {can_delete &&
                                (confirming ? (
                                    <span className="flex items-center gap-2">
                                        <button
                                            data-testid="memory-delete-confirm"
                                            onClick={remove}
                                            className="rounded-full bg-red-700 px-5 py-2.5 text-xs font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                                        >
                                            Confirm delete
                                        </button>
                                        <button
                                            data-testid="memory-delete-cancel"
                                            onClick={() => setConfirming(false)}
                                            className="rounded-full border-2 border-warmgray px-5 py-2.5 text-xs font-semibold text-navy"
                                        >
                                            Keep it
                                        </button>
                                    </span>
                                ) : (
                                    <button
                                        data-testid="memory-delete-button"
                                        onClick={() => setConfirming(true)}
                                        className="flex items-center gap-2 rounded-full border-2 border-warmgray px-5 py-2.5 text-sm font-semibold text-navy/70 transition-colors duration-300 hover:border-red-700 hover:text-red-700"
                                    >
                                        <Trash2 size={15} /> Delete
                                    </button>
                                ))}
                        </div>
                    </div>

                    <section data-testid="comments-section" className="mt-10">
                        <h2 className="font-display text-2xl">Family voices</h2>
                        <p className="mt-1 text-sm text-[#2C3347]">Share what this memory means to you — in writing or with your own voice.</p>

                        <div className="mt-5 space-y-4">
                            {comments.length === 0 && (
                                <p data-testid="comments-empty" className="rounded-2xl border-2 border-dashed border-warmgray bg-ivory p-6 text-center text-sm text-navy/70">
                                    No comments yet. Be the first to add yours.
                                </p>
                            )}
                            {comments.map((c) => (
                                <div key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="flex gap-3 rounded-2xl border-2 border-warmgray bg-ivory p-4">
                                    <Avatar person={{ name: c.author_name, picture: c.author_picture, avatar_path: c.author_avatar_path }} size={36} />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold">
                                            {c.author_name}
                                            <span className="ml-2 font-mono text-[10px] font-normal uppercase tracking-widest text-navy/50">
                                                {formatDate(c.created_at)}
                                            </span>
                                        </p>
                                        {c.text && <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-navy">{c.text}</p>}
                                        {c.audio_path && (
                                            <audio data-testid={`comment-audio-${c.comment_id}`} src={`${API}/files/${c.audio_path}`} controls className="mt-2 w-full max-w-sm" />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <form onSubmit={sendComment} className="mt-5 rounded-2xl border-2 border-warmgray bg-ivory p-4">
                            <textarea
                                data-testid="comment-text-input"
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder="Write a comment…"
                                aria-label="Comment"
                                rows={2}
                                className="w-full resize-none rounded-xl border-2 border-warmgray bg-[#FDFBF9] px-4 py-3 text-sm text-navy outline-none transition-colors duration-300 focus:border-peach"
                            />
                            {commentFile && (
                                <p data-testid="comment-recording-ready" className="mt-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-navy">
                                    Voice note ready
                                    <button type="button" data-testid="comment-recording-remove" onClick={() => setCommentFile(null)} aria-label="Remove voice note" className="text-red-700">
                                        <X size={13} />
                                    </button>
                                </p>
                            )}
                            {recording && (
                                <p data-testid="comment-recording-indicator" className="mt-2 font-mono text-[11px] uppercase tracking-widest text-red-700">
                                    ● Recording your voice comment…
                                </p>
                            )}
                            <div className="mt-3 flex items-center justify-between">
                                <button
                                    type="button"
                                    data-testid="comment-record-button"
                                    onClick={recording ? stopRecording : startRecording}
                                    className={`flex items-center gap-2 rounded-full border-2 px-4 py-2 text-xs font-semibold transition-colors duration-300 ${
                                        recording ? "border-red-700 bg-red-700 text-white" : "border-warmgray text-navy hover:border-peach"
                                    }`}
                                >
                                    <Mic size={14} /> {recording ? "Stop" : "Voice comment"}
                                </button>
                                <button
                                    type="submit"
                                    data-testid="comment-send-button"
                                    disabled={sending || (!commentText.trim() && !commentFile)}
                                    className="btn-gradient flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                                >
                                    {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
                                </button>
                            </div>
                        </form>
                    </section>
                </motion.div>
            </div>
        </main>
    );
}
